/** Consent state shared by the banner and app analytics. Defaults run in HTML
 * before GTM; this module updates that same gtag queue and replays SDK consent.
 * Persisted choices expire after 365 days. Storage failure is session-only.
 */
const KEY = "btk_consent_v1";
const TTL_MS = 365 * 24 * 60 * 60 * 1000;
let sessionChoice;
let listeners = [];
const safeWindow = () => (typeof window !== "undefined" ? window : null);
export const getConsent = () => {
  if (sessionChoice !== undefined) return sessionChoice;
  const w = safeWindow();
  if (!w) return null;
  try {
    const parsed = JSON.parse(w.localStorage.getItem(KEY));
    if (!parsed || !["granted", "denied"].includes(parsed.value) || !Number.isFinite(parsed.ts)) return null;
    const age = Date.now() - parsed.ts;
    if (age < 0 || age > TTL_MS) return null;
    return parsed.value;
  } catch (_) { return null; }
};

export const pushDefaultConsent = () => {
  const w = safeWindow();
  if (!w) return;
  w.dataLayer = w.dataLayer || [];
  if (typeof w.gtag !== "function") w.gtag = function () { w.dataLayer.push(arguments); };
  if (w.__btkConsentDefault) return;
  w.gtag("consent", "default", {
    ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied",
    analytics_storage: "denied", functionality_storage: "granted", security_storage: "granted",
    wait_for_update: 500,
  });
  w.__btkConsentDefault = true;
};
const propagate = (value) => {
  const w = safeWindow();
  if (!w) return;
  pushDefaultConsent();
  const state = value === "granted" ? "granted" : "denied";
  w.gtag("consent", "update", {
    ad_storage: state, ad_user_data: state, ad_personalization: state, analytics_storage: state,
  });
  try {
    if (typeof w.fbq === "function") w.fbq("consent", state === "granted" ? "grant" : "revoke");
  } catch (_) { /* one unavailable SDK must not block other consent updates */ }
  try { if (w.posthog) {
    // Do not start session recording or emit $opt_in (which includes raw URL).
    if (typeof w.posthog.stopSessionRecording === "function") w.posthog.stopSessionRecording();
    if (state === "granted" && typeof w.posthog.opt_in_capturing === "function") w.posthog.opt_in_capturing({ captureEventName: false });
    if (state === "denied" && typeof w.posthog.opt_out_capturing === "function") w.posthog.opt_out_capturing();
  } } catch (_) { /* consent subscribers must still update the UI */ }
};
export const setConsent = (value) => {
  if (!["granted", "denied"].includes(value)) return;
  sessionChoice = value;
  const w = safeWindow();
  try { if (w) w.localStorage.setItem(KEY, JSON.stringify({ value, ts: Date.now() })); } catch (_) { /* session choice still works */ }
  propagate(value);
  listeners.forEach((cb) => { try { cb(value); } catch (_) { /* isolated subscribers */ } });
};
export const onConsentChange = (cb) => {
  listeners.push(cb);
  return () => { listeners = listeners.filter((x) => x !== cb); };
};
// Queue calls into stubs too: SDKs loaded after acceptance receive the choice.
export const replayConsent = () => propagate(getConsent());
